<template>
  <div class="pot-sm-container">
    <slot name="top-highlight" />
    <GridContainer padding>
      <GridRow
        class="pot-sm-container__row"
        clear-padding
      >
        <GridColumn
          v-for="(teaser, index) in teasersWithOffer"
          :key="index"
          :sm="tileGridSize"
          class="pot-sm-container__column"
        >
          <ProductOfferTile
            :animation-delay="setDelayAnimation(teaser.animationDelay || 0, index)"
            :background-color="teaser.bgColor"
            :description="teaser.copy"
            :heading-tag="headingTag"
            :offer-alternatives="teaser.offerAlternatives"
            :tile-size="tileSize"
            :title="teaser.teaserTitle"
            :show-available-variant="showAvailableVariant"
            :animation-init-timeout="animationInitTimeout"
            :brand="teaser.brand"
            price-variant="medium"
            animate-tile
            no-color-title
            link-tag="nuxt-link"
            class="pot-sm-container__offer"
            :data-articlenumber="teaser?.offer?.product?.articleNumber"
          >
            <template #button="{ activeVariant }">
              <AddToCartButtonContainer
                v-if="activeVariant?.offer"
                ref="cartButton"
                :display-text="false"
                :offer="activeVariant.offer"
                :product-name="activeVariant.offer?.product?.name ?? undefined"
              />
            </template>
          </ProductOfferTile>
        </GridColumn>
      </GridRow>
    </GridContainer>
  </div>
</template>

<script lang="ts" setup>
import {
  GridColumn,
  GridRow,
  GridContainer,
  ProductOfferTile,
} from '@i22team/td-longtail-vue-components';
import { getFirstOfferAlternatives, type TransformedAlternative } from '@i22team/rocket';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';
import type { Promotion, PromotionOffer } from '~/lib/offers-by-group';
import { getBrand, type BrandWithLogo } from '~/lib/brand';

export type ProductOfferTileSMPromotion = Promotion<{
  animationDelay?: number;
  showLocalTitle?: boolean;
  title?: string;
  bgColor?: string;
  copy?: string;
}>;

export type ProductOfferTileSMContainerProps = {
  items?: PromotionOffer<ProductOfferTileSMPromotion>[];
  noDelayAnimation?: boolean;
  headingTag?: string;
  tileType?: 'small' | 'medium' | undefined;
  showAvailableVariant?: boolean;
  showBrandLogo?: boolean;
  animationInitTimeout?: number;
};

const props = withDefaults(defineProps<ProductOfferTileSMContainerProps>(), {
  tileType: 'small',
  items: undefined,
  headingTag: undefined,
  animationInitTimeout: undefined,
});

const tileGridSize = computed(() => (props.tileType === 'small' ? 4 : 6));
const tileSize = computed(() => (props.tileType === 'small' ? 's' : 'm'));

const setDelayAnimation = (delay: number, index: number) => {
  if (props.noDelayAnimation || delay === 0) return 0;
  if (delay >= 0) return delay;

  if (index >= 0) {
    return (index % (props.tileType === 'small' ? 3 : 2)) + 1;
  }

  console.warn('index is required');
  return 0;
};

type TeasersWithOffer = PromotionOffer<ProductOfferTileSMPromotion> & {
  offerAlternatives: TransformedAlternative<ListOfferProductAlternative>;
  teaserTitle: string;
  brand: BrandWithLogo | undefined;
};
const teasersWithOffer = computed<TeasersWithOffer[]>(
  () =>
    props.items?.map((promotion: PromotionOffer<ProductOfferTileSMPromotion>) => ({
      ...promotion,
      teaserTitle:
        promotion.showLocalTitle && promotion.title
          ? promotion.title
          : promotion.offer?.product?.family?.name || '',
      offerAlternatives: getFirstOfferAlternatives(
        (promotion.offer?.productAlternatives || []).filter(Boolean)
      ),
      brand: props.showBrandLogo ? getBrand(promotion.offer) : undefined,
    })) || []
);

defineExpose({
  tileGridSize,
  tileSize,
  setDelayAnimation,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.pot-sm-container {
  :deep(.product-offer-color-select__item) {
    padding-bottom: 6px;
  }

  :deep(.price__amount--highlight) {
    font-weight: 700;
  }

  :deep(.product-offer-color-select__list) {
    flex-wrap: wrap;
    max-width: 100%;
  }

  :deep(.product-offer-detail-link__link.link-button) {
    display: block;
  }

  &__column {
    margin-bottom: $grid-base;
    @include mq($mq-sm) {
      margin-bottom: $grid-base * 2;
    }
  }
}
</style>
